import React, { useState, useEffect } from "react";
import { LogEvent } from "@learningblocks/shared-components/lib/EventLogger";
import { useTranslation } from "react-i18next";

import Hex from "../Hex";

import "./index.css";

export default function NavigationMain(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");

  const [currentActive, setCurrentActive] = useState(0); // For label element active conditions
  const [endReached, setEndReached] = useState(false);

  useEffect(() => {
    updateSteps();
  }, [props.percentage]);

  const logLatestSection = (type) => {
    if (type === "end" && !endReached && props.loaded) {
      setEndReached(true);
    }
  };

  const updateSteps = () => {
    const customSteps = props.sceneData.Scrolly.customSections;

    let firstCustomPercent;
    if (customSteps.length === 0) {
      // if there are no custom labels, use default end
      firstCustomPercent = 100;
    } else {
      const firstCustomSeconds = toSeconds(customSteps[0]);
      firstCustomPercent = (firstCustomSeconds / props.totalTime) * 100;
    }

    if (props.percentage < firstCustomPercent) {
      // If from start / before first custom step
      setCurrentActive(0);
    } else if (props.percentage === 100) {
      // If at the end
      setCurrentActive(customSteps.length + 1);
    } else {
      let lastActive = 0;
      let percent;

      for (let i = 0; i < customSteps.length; i++) {
        // find the max active step/label
        const seconds = toSeconds(customSteps[i]);
        percent = (seconds / props.totalTime) * 100;
        if (
          Math.round(percent * 1000) / 1000 <=
          Math.round(props.percentage * 1000) / 1000
        ) {
          // increase accuracy leniency
          lastActive++;
        }
      }

      if (lastActive > 8) {
        lastActive = 8;
      } // if max reached, set limit
      setCurrentActive(lastActive);
    }
  };

  const forceAnimationUpdate = (e, percent) => {
    // Hanlde navigation interction (previous and next conditions)
    const keycode = e.keyCode ? e.keyCode : e.which;
    if (keycode === 13 || e === "click") {
      // Detected interaction (by click or enter with keyboard)

      if (percent === "start") {
        window.scrollTo(0, 0);
      } else if (percent === "end") {
        window.scrollTo(0, document.body.scrollHeight);
      } else {
        props.forceAnimationTo(percent);
      }
    }
  };

  const splashLabel = (position) => {
    return (
      <div
        className="splash-label"
        tabIndex={0}
        role="button"
        aria-label={`${position === "end" ? `${t("goTo.string")}: ${t("end.string")}` : `${t("goTo.string")}: ${t("start.string")}`}`}
        onKeyUp={(e) => {
          forceAnimationUpdate(e, position);
          props.forceScroll("nav");
        }} // Detect Enter
        onClick={() => {
          forceAnimationUpdate("click", position);
          props.forceScroll("nav");
        }} // Detect Click and set scroll override
        style={{
          position: position === "end" ? "relative" : null,
          top: position === "end" ? "2px" : null,
        }}
      >
        <LogEvent
          logEventProps={
            // This handles specific triggers for start and end sections
            position === "start" && props.loaded
              ? {
                  // Always start when not "end"
                  event_type: "scr_section",
                  event_schema: "scrollableSection",
                  current: "start",
                  total: `${props.sceneData.Scrolly.customSections.length}`,
                  percent: `${props.percentage.toFixed(2)}%`,
                }
              : !endReached && props.loaded
                ? {
                    event_type: "scr_end",
                    event_schema: "scrollableEnd",
                  }
                : null
          }
          actionProps={{ logLatestSection: { action: "viewed" } }}
          elementType="div"
        >
          <Hex
            type={position}
            currentActive={currentActive}
            index={position}
            percent={props.percentage}
            logLatestSection={logLatestSection}
            logStart={props.logStart}
          />
        </LogEvent>
        <div className="label-text" aria-hidden="true">
          {position === "start" ? (
            <svg
              className="start"
              xmlns="http://www.w3.org/2000/svg"
              fill="rgb(0,62,107)"
              width="14"
              height="14"
              viewBox="0 0 24 24"
            >
              <path
                d="M2 24v-24l20 12-20 12z"
                focusable="false"
                aria-hidden="true"
              />
            </svg>
          ) : position === "end" ? (
            <svg
              className="checkmark"
              xmlns="http://www.w3.org/2000/svg"
              fill="rgb(0,62,107)"
              width="14"
              height="14"
              viewBox="0 0 24 24"
            >
              <path
                d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"
                focusable="false"
                aria-hidden="true"
              />
            </svg>
          ) : null}
        </div>
      </div>
    );
  };

  const customLabels = (time, key) => {
    const seconds = toSeconds(time);
    const percent = (seconds / props.totalTime) * 100;
    return (
      <div
        id={`custom-label-${key}`}
        className="custom-label"
        role="button"
        aria-label={`${t("goTo.string")}: ${t("section.string")} ${key + 1}`}
        tabIndex={0}
        key={key}
        onKeyUp={(e) => {
          forceAnimationUpdate(e, percent);
          props.forceScroll("nav");
        }}
        onClick={() => {
          forceAnimationUpdate("click", percent);
          props.forceScroll("nav");
        }}
        style={{ display: key > 7 ? "none" : null }} // display limit of 8 custom labels
      >
        <LogEvent
          logEventProps={{
            // This handles numbered sections
            event_type: "scr_section",
            event_schema: "scrollableSection",
            current: `${currentActive}`,
            total: `${props.sceneData.Scrolly.customSections.length}`,
            percent: `${props.percentage.toFixed(2)}%`,
          }}
          actionProps={{ logLatestSection: { action: "viewed" } }}
          elementType="div"
        >
          <Hex
            type={"custom"}
            currentActive={currentActive}
            index={key}
            logLatestSection={logLatestSection}
          />
        </LogEvent>
        <div className="label-text" aria-hidden="true">
          {key + 1}
        </div>
      </div>
    );
  };

  const toSeconds = (timeStamp) => {
    // "00:00.00" (minutes:seconds) to seconds for percentage calculations and comparisons
    const timeArr = timeStamp.split(":");
    const seconds = Number(timeArr[0]) * 60 + Number(timeArr[1]);
    return seconds;
  };

  return (
    <div
      role="navigation"
      aria-label={t("chapterNavigation.string")}
      id="navigation-container"
      className="navigation-container"
    >
      <div
        id="progressBar"
        className="progressBar"
        role="none"
        style={{
          background: "rgba(255, 255, 255, 0.5)", // Grey bar that doesn't change color
        }}
      >
        {splashLabel("start")}
        {/* Render custom labels */}
        {props.sceneData.Scrolly.customSections.map((time, key) =>
          customLabels(time, key)
        )}
        {splashLabel("end")}
      </div>
    </div>
  );
}
